import { useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { useCarCatalogue } from '../../application/cars/useCarCatalogue';
import useCarCollection from '../../application/game/useCarCollection';
import useOvertakeInvite from '../../application/game/useOvertakeInvite';
import useOvertakeMatch from '../../application/game/useOvertakeMatch';
import AccountSheet from '../account/AccountSheet';
import OvertakeBoard from '../game/OvertakeBoard';
import OvertakeInvite from '../game/OvertakeInvite';
import OvertakeSetup from '../game/OvertakeSetup';
import { WheelIcon } from '../icons/ControlIcons';

/**
 * The Overtake (SPEC-011) — the climbing card game the duel is one question of.
 *
 * A composition, and since PR-11.4 the composition's real job is choosing which
 * of two matches is on screen. Both are the same game drawn by the same
 * `OvertakeBoard`; they differ only in where the state lives and which deck it
 * was dealt from:
 *
 *   LOCAL     `useOvertakeMatch`, against the app, dealt from the reader's OWN
 *             cars (SPEC-010). No account, no network, works offline.
 *   INVITED   `useOvertakeInvite`, against somebody else's phone, dealt from the
 *             WHOLE catalogue and held in one document both devices watch
 *             (ADR-0006). Needs an account, and says so.
 *
 * The deck difference is the one thing here that would be a bug if it were
 * tidied away. A local match dealt from the whole catalogue would let a reader
 * spend a car they have not won; an invited match dealt from one player's
 * collection would deal cards the other one cannot see.
 *
 * A match id in the query string is what decides between them — `?match=…`, the
 * same place the comparison keeps its pair, because it is the thing worth
 * sending somebody.
 */
export default function PlayScreen() {
    const t = useTranslate();
    const { cars, status } = useCarCatalogue();
    const { unlocked } = useCarCollection({ cars });
    const local = useOvertakeMatch({ cars: unlocked });
    const invite = useOvertakeInvite({ cars });
    const [isAccountOpen, setAccountOpen] = useState(false);

    if (status === 'loading') {
        return <PlayEmptyState message={t('play.loading')} />;
    }

    if (status === 'failed') {
        return <PlayEmptyState message={t('play.failed')} />;
    }

    // Mounted only while it is open, so `showModal()` runs once per opening and
    // the dialog carries nothing between them — the shape `DuelScreen` uses.
    const account = isAccountOpen
        ? <AccountSheet onClose={() => setAccountOpen(false)} />
        : null;
    // The screen's own heading. The composition has no room for a visible title
    // — the game is what the reader is here for — but a screen still has to be
    // announced and navigated by one. Only the pixels are gone.
    const heading = (
        <div className="visually-hidden">
            <h1>{t('play.title')}</h1>
            <p>{t('play.intro')}</p>
        </div>
    );

    // --- An invitation takes the whole screen -----------------------------
    if (invite.matchId) {
        const document = invite.status === 'ready' ? invite.document : null;
        const isSeated = Boolean(document?.seats.opponent) && Boolean(invite.seat);

        return (
            <section className={`overtake${isSeated ? ' overtake--match' : ''}`}>
                {heading}
                {isSeated ? (
                    <OvertakeBoard
                        match={document.state}
                        deck={invite.deck}
                        mySide={invite.seat}
                        /* There is no thinking pause across a network. The wait
                           is real, and it ends when the other person moves. */
                        isWaiting={document.state.turn !== invite.seat}
                        onMove={invite.move}
                        /* Again means a NEW invitation: the id IS the match, so
                           replaying deals a fresh one and a fresh link to send. */
                        onAgain={() => invite.create({ leadRule: 'alternate' })}
                        onLeave={invite.leave}
                    />
                ) : (
                    <OvertakeInvite invite={invite} onSignIn={() => setAccountOpen(true)} />
                )}
                {account}
            </section>
        );
    }

    // --- Nothing dealt yet: the setup, and the offer to invite somebody ----
    if (!local.match) {
        return (
            <section className="overtake">
                {heading}
                <OvertakeSetup onDeal={local.deal} />
                <OvertakeInvite
                    invite={invite}
                    onSignIn={() => setAccountOpen(true)}
                    /* Alternating, always. Winner-leads is the right default
                       against the app, where a run building is the point; across
                       two people it lets whoever takes the first two pots name
                       every remaining figure, which is a runaway rather than a
                       streak. */
                    onCreate={() => invite.create({ leadRule: 'alternate' })}
                />
                {account}
            </section>
        );
    }

    // A collection too small to deal from. It grows, and the catalogue is
    // fetched, so this is a designed state rather than an impossible one.
    if (local.match.status === 'unavailable') {
        return <PlayEmptyState message={t('play.unavailable')} />;
    }

    return (
        /* A MATCH FITS THE SCREEN AND DOES NOT SCROLL (D-29). The modifier is
           what turns the column into a fixed one: the setup and the invitation
           notices above are short and may grow with their copy, and a board is a
           table you have to see all of at once. */
        <section className="overtake overtake--match">
            {heading}
            <OvertakeBoard
                match={local.match}
                deck={local.deck}
                isWaiting={local.isWaiting}
                onMove={local.move}
                onAgain={local.redeal}
                onLeave={local.leave}
            />
        </section>
    );
}

/**
 * The states with no match in them.
 *
 * The same shape the garage and the duel use, because it is the same situation:
 * a screen that cannot show its content still has to feel like the product
 * rather than like a stack trace somebody caught.
 */
function PlayEmptyState({ message }) {
    return (
        <section className="overtake overtake--empty">
            <div className="empty-state">
                <WheelIcon className="empty-state__mark" size={32} />
                <p className="empty-state__message">{message}</p>
            </div>
        </section>
    );
}
